import { useEffect, useRef, useState } from "react";
import { api, jobStreamUrl } from "./api";
import type { Job } from "./types";

/**
 * Subscribe to a job's progress over SSE.
 *
 * The stream's first event is the job's persisted state, so a late or
 * reconnecting client is immediately correct. If the connection drops before
 * the job reaches a terminal state we fall back to polling rather than leaving
 * the UI frozen on a stale percentage — a long generation run outliving one
 * flaky connection should not look like a hang.
 */
export function useJobStream(jobId: string | null, onDone?: (job: Job) => void) {
  const [job, setJob] = useState<Job | null>(null);
  const doneRef = useRef(false);
  const callbackRef = useRef(onDone);
  callbackRef.current = onDone;

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      return;
    }
    doneRef.current = false;
    let source: EventSource | null = null;
    let pollTimer: number | null = null;
    let cancelled = false;

    const settle = (next: Job) => {
      setJob(next);
      const terminal =
        next.status === "succeeded" || next.status === "failed" || next.status === "cancelled";
      if (terminal && !doneRef.current) {
        doneRef.current = true;
        callbackRef.current?.(next);
        source?.close();
        if (pollTimer) window.clearInterval(pollTimer);
      }
    };

    const startPolling = () => {
      if (pollTimer || doneRef.current || cancelled) return;
      pollTimer = window.setInterval(async () => {
        try {
          const next = await api.getJob(jobId);
          if (!cancelled) settle(next);
        } catch {
          /* keep trying; the backend may be restarting */
        }
      }, 2000);
    };

    try {
      source = new EventSource(jobStreamUrl(jobId));
      source.onmessage = (event) => {
        if (cancelled) return;
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === "heartbeat") return;
          settle(payload as Job);
        } catch {
          /* ignore malformed frame */
        }
      };
      source.onerror = () => {
        source?.close();
        startPolling();
      };
    } catch {
      startPolling();
    }

    return () => {
      cancelled = true;
      source?.close();
      if (pollTimer) window.clearInterval(pollTimer);
    };
  }, [jobId]);

  return job;
}
